import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { X } from 'lucide-react'

type ModalSize = 'md' | 'lg' | 'xl'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  size?: ModalSize
  /**
   * Un clic în afara ferestrei o închide.
   *
   * Implicit da — e comportamentul așteptat la o fereastră în care doar te uiți. La una în care
   * SE COMPLETEAZĂ ceva se dă `false`: altfel un clic alături șterge tot ce s-a scris, fără
   * avertisment și fără cale înapoi. Semnalat pe formularul de cerere de marfă, unde se tastează
   * cantități produs cu produs (Alexandru, 22.09.2026).
   *
   * `Escape` și butonul de renunțare rămân, în ambele cazuri.
   */
  inchideLaClicInAfara?: boolean
}

const SIZE_CLASS: Record<ModalSize, string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-5xl',
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  inchideLaClicInAfara = true,
}: ModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={inchideLaClicInAfara ? onClose : undefined}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-modal
        className={`w-full ${SIZE_CLASS[size]} max-h-[90vh] overflow-y-auto rounded-lg border border-b4l-border bg-b4l-surface-ridicata shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {/*
          Antetul rămâne lipit sus, cu un X mereu la vedere.

          Fără el, o fereastră cu listă lungă nu are ieșire vizibilă: butoanele de jos cad sub
          marginea ecranului, iar de când clicul alături nu mai închide, singura cale rămasă era
          `Escape` — pe care nu ți-o spune nimeni. Semnalat la transferul cu tot catalogul de
          produse (Alexandru, 22.09.2026).
        */}
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-b4l-border bg-b4l-surface-ridicata px-4 py-3">
          <h3 className="flex-1 text-sm font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Închide"
            title="Închide (Esc)"
            className="rounded p-1 text-b4l-text-muted hover:bg-b4l-bg hover:text-b4l-text"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-4 py-4">{children}</div>
        {footer && (
          <div className="sticky bottom-0 flex justify-end gap-2 border-t border-b4l-border bg-b4l-bg-soft px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
